import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { useAuth } from './AuthContext';
import DatabaseService from '../utils/databaseService';

const ScheduleContext = createContext();

const initialState = {
  employees: [],
  schedules: [], // Now always empty - no persistence
  currentSchedule: null,
  viewMode: 'horizontal',
  settings: {
    shifts: {
      früh: { start: '07:00', end: '15:54', hours: 8, minutes: 24 },
      zwischen: { start: '10:00', end: '18:54', hours: 8, minutes: 24 },
      spät: { start: '12:45', end: '21:39', hours: 8, minutes: 54 },
    },
    minStaffing: {
      weekday: { früh: 2, zwischen: 3, spät: 2 },
      weekend: { früh: 1, zwischen: 2, spät: 1 },
    },
    rules: {
      minRestHours: 11,
      noEarlyAfterLate: true,
      maxHoursPerDay: 12,
      maxHoursPerWeek: 48,
      hoursTolerance: 8,
      minutesTolerance: 0,
      minDaysOffBetweenBlocks: 2,
    },
    weekendRules: { under50: 1, over50: 2 },
    holidays: [],
  },
  loading: false,
  error: null,
};

function scheduleReducer(state, action) {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload };
    case 'SET_EMPLOYEES':
      return { ...state, employees: action.payload };
    case 'SET_SETTINGS':
      return { ...state, settings: { ...state.settings, ...action.payload } };
    case 'ADD_EMPLOYEE':
      return {
        ...state,
        employees: [
          ...state.employees,
          {
            ...action.payload,
            id: action.payload.id || Date.now(),
            maxConsecutiveDays: action.payload.maxConsecutiveDays || 4,
            sickLeave: action.payload.sickLeave || { from: '', to: '' },
            vacation: action.payload.vacation || { from: '', to: '' },
            availableDays: action.payload.availableDays || {
              monday: true,
              tuesday: true,
              wednesday: true,
              thursday: true,
              friday: true,
              saturday: true,
              sunday: true,
            },
          },
        ],
      };
    case 'UPDATE_EMPLOYEE':
      return {
        ...state,
        employees: state.employees.map((emp) =>
          emp.id === action.payload.id ? { ...emp, ...action.payload } : emp
        ),
      };
    case 'DELETE_EMPLOYEE':
      return {
        ...state,
        employees: state.employees.filter((emp) => emp.id !== action.payload),
      };
    case 'SET_CURRENT_SCHEDULE':
      // No persistence - just set in state
      return { ...state, currentSchedule: action.payload };
    case 'UPDATE_SETTINGS':
      return { ...state, settings: { ...state.settings, ...action.payload } };
    case 'UPDATE_WEEKEND_RULES':
      return {
        ...state,
        settings: {
          ...state.settings,
          weekendRules: { ...state.settings.weekendRules, ...action.payload },
        },
      };
    case 'ADD_HOLIDAY':
      return {
        ...state,
        settings: {
          ...state.settings,
          holidays: [...state.settings.holidays, action.payload],
        },
      };
    case 'DELETE_HOLIDAY':
      return {
        ...state,
        settings: {
          ...state.settings,
          holidays: state.settings.holidays.filter((h) => h.id !== action.payload),
        },
      };
    case 'SET_VIEW_MODE':
      return { ...state, viewMode: action.payload };
    case 'RESET_DEPARTMENT_DATA':
      // Simplified - just reset to initial state
      return { ...initialState, viewMode: state.viewMode };
    default:
      return state;
  }
}

export function ScheduleProvider({ children }) {
  const [state, dispatch] = useReducer(scheduleReducer, initialState);
  const { currentDepartment, isAuthenticated } = useAuth();

  // Load department data (only employees and settings - no schedules)
  useEffect(() => {
    let isCancelled = false;

    const loadDepartmentData = async (department) => {
      if (!department) {
        console.warn('ScheduleContext: No department provided for loading data');
        return;
      }

      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'SET_ERROR', payload: null });

      try {
        console.log(`ScheduleContext: Loading data for department: ${department.name} (${department.id})`);
        
        // Ensure localStorage has current department ID
        const storedDeptId = localStorage.getItem('currentDepartmentId');
        if (storedDeptId !== department.id) {
          localStorage.setItem('currentDepartmentId', department.id);
        }

        const [employees, settings] = await Promise.all([
          DatabaseService.loadEmployees(department.id),
          DatabaseService.loadSettings(department.id),
        ]);

        if (isCancelled) {
          console.log(`ScheduleContext: Aborted data processing for department: ${department.name}`);
          return;
        }

        console.log(`ScheduleContext: Data loaded - ${employees?.length || 0} employees for ${department.name}`);
        dispatch({ type: 'SET_EMPLOYEES', payload: employees || [] });
        if (settings) {
          dispatch({ type: 'SET_SETTINGS', payload: settings });
        }
      } catch (error) {
        if (!isCancelled) {
          console.error('ScheduleContext: Error loading department data:', error);
          dispatch({ type: 'SET_ERROR', payload: 'Failed to load department data: ' + error.message });
          dispatch({ type: 'SET_EMPLOYEES', payload: [] });
        }
      } finally {
        if (!isCancelled) {
          dispatch({ type: 'SET_LOADING', payload: false });
        }
      }
    };

    if (isAuthenticated && currentDepartment) {
      console.log(`ScheduleContext: Department changed to: ${currentDepartment.name}. Triggering data reload.`);
      loadDepartmentData(currentDepartment);
    } else {
      console.log('ScheduleContext: No authenticated user or department. Resetting data.');
      dispatch({ type: 'RESET_DEPARTMENT_DATA' });
    }

    return () => {
      isCancelled = true;
      console.log(`ScheduleContext: Cleanup triggered for department: ${currentDepartment?.name}`);
    };
  }, [currentDepartment, isAuthenticated]);

  // Auto-save employees (unchanged)
  useEffect(() => {
    const saveEmployees = async () => {
      if (!currentDepartment || state.loading) return;
      try {
        const savePromises = state.employees.map((employee) =>
          DatabaseService.saveEmployee(employee, currentDepartment.id)
        );
        await Promise.all(savePromises);
      } catch (error) {
        console.error('Error auto-saving employees:', error);
      }
    };

    if (state.employees.length > 0 && currentDepartment && !state.loading) {
      const timeoutId = setTimeout(saveEmployees, 1000);
      return () => clearTimeout(timeoutId);
    }
  }, [state.employees, currentDepartment, state.loading]);

  // Auto-save settings (unchanged)
  useEffect(() => {
    const saveSettings = async () => {
      if (!currentDepartment || state.loading) return;
      try {
        await DatabaseService.saveSettings(state.settings, currentDepartment.id);
      } catch (error) {
        console.error('Error auto-saving settings:', error);
      }
    };

    if (currentDepartment && !state.loading) {
      const timeoutId = setTimeout(saveSettings, 1000);
      return () => clearTimeout(timeoutId);
    }
  }, [state.settings, currentDepartment, state.loading]);

  return (
    <ScheduleContext.Provider value={{ state, dispatch }}>
      {children}
    </ScheduleContext.Provider>
  );
}

export function useSchedule() {
  const context = useContext(ScheduleContext);
  if (!context) {
    throw new Error('useSchedule must be used within a ScheduleProvider');
  }
  return context;
}